/**
 * AI/email provider types + the PROVIDER_REGISTRY catalog.
 *
 * PROVIDER_REGISTRY is now DERIVED from the unified `CREDENTIAL_REGISTRY`
 * (credential.ts) — the single source of truth for every credential-bearing
 * integration. This file keeps the provider-facing TYPES (ProviderId,
 * ProviderConfig, ProviderMeta) and adapts the AI/email subset of the unified
 * registry into the ProviderMeta shape existing consumers expect, so nothing
 * downstream changes while the credential model is unified underneath.
 */
import { CREDENTIAL_REGISTRY, type CredentialId } from './credential.js';

// ---------------------------------------------------------------------------
// Provider configuration (direct API — Anthropic SDK + Ollama)
// ---------------------------------------------------------------------------

/**
 * AI/email provider ids — the credential ids MINUS the three infra services.
 * Derived from CredentialId (credential.ts is the SSOT) so the two id unions
 * cannot drift; type-identical to the former hand-written union.
 */
export type ProviderId = Exclude<CredentialId, 'cloudflare' | 'porkbun' | 'github'>;

export interface ProviderConfig {
  anthropic?: {
    apiKey: string;
  };
  openai?: {
    apiKey: string;
  };
  ollama?: {
    baseUrl: string;
  };
  groq?: {
    apiKey: string;
  };
  openrouter?: {
    apiKey: string;
  };
  mistral?: {
    apiKey: string;
  };
  gemini?: {
    apiKey: string;
  };
  agentmail?: {
    apiKey: string;
    inbox: string;
  };
}

/** Provider metadata for UI display and API routing. */
export interface ProviderMeta {
  id: ProviderId;
  name: string;
  description: string;
  baseUrl: string;
  logo: string;
  requiresApiKey: boolean;
  keyPrefix?: string;
  keyPlaceholder?: string;
  /**
   * Where a user obtains an API key for this provider ("Get a key" link). Part
   * of the catalog SSOT so onboarding + Connections render the same link without
   * a parallel list. Omit for providers with no key (Ollama) or none
   * (AgentMail is configured via Settings).
   */
  keyUrl?: string;
  /** Drives the Connections page section split — AI providers vs email services. */
  category: 'ai' | 'email';
  /**
   * Wire transport the provider factory uses to instantiate a ModelProvider.
   * SINGLE SOURCE OF TRUTH for model-provider routing — `createProvider`
   * switches on this, so adding or retargeting a provider is a registry edit,
   * not a new factory branch. `'none'` = not a model provider (e.g. the
   * AgentMail email service, which is never passed to the factory).
   */
  transport: 'anthropic-sdk' | 'openai-compat' | 'ollama' | 'none';
}

/**
 * The AI/email provider catalog — DERIVED from CREDENTIAL_REGISTRY (the SSOT).
 *
 * We take the credential specs in the 'ai' and 'email' groups (array order
 * preserved — display order is significant) and adapt each into ProviderMeta:
 *   - requiresApiKey ← any secret field present
 *   - keyPrefix / keyPlaceholder ← the first SECRET field's prefix / placeholder
 *   - keyUrl ← credentialUrl; category ← group; transport passes through
 * The infra group (Cloudflare/Porkbun/GitHub) is intentionally excluded — those
 * are non-provider credentials handled by the infra broker, not model routing.
 */
export const PROVIDER_REGISTRY: ProviderMeta[] = CREDENTIAL_REGISTRY.filter(
  (c) => c.group === 'ai' || c.group === 'email',
).map((c): ProviderMeta => {
  const secretField = c.fields.find((f) => f.secret);
  const meta: ProviderMeta = {
    id: c.id as ProviderId,
    name: c.label,
    description: c.description,
    baseUrl: c.baseUrl,
    logo: c.logo,
    requiresApiKey: secretField !== undefined,
    category: c.group === 'email' ? 'email' : 'ai',
    transport: c.transport,
  };
  if (secretField?.validatePrefix !== undefined) meta.keyPrefix = secretField.validatePrefix;
  if (secretField?.placeholder !== undefined) meta.keyPlaceholder = secretField.placeholder;
  if (c.credentialUrl !== undefined) meta.keyUrl = c.credentialUrl;
  return meta;
});
